import { FC } from "react";
import {
	ArrowContainer,
	ArrowIcon,
	CardTooltip,
	DetailsListItem,
	DetailsListItemParagraph,
	DetailsListItemStrong,
	DetailsListItemTwoColumns,
	Eta,
	HeaderLeft,
	HeaderRight,
	ProcessedTrucksCardWrapper,
	StatusChip,
	StatusEtaContainer,
	TruckDetails,
	TruckHeader,
	TruckIdContainer
} from "./ProcessedTrucksCard.styles";
import { convertToTitleCase } from "utils/string";
import {
	AccountNumberText,
	CabinetCountText,
	CompleteTooltipText,
	ETAText,
	InProductionTooltipText,
	OrderDateText,
	ProductLineText,
	TruckNameText,
	TruckPendingText,
	UnavailableDataPlaceholderText
} from "constants/text";
import { models } from "types/api/viewModels";
import { dateFormatTwoDigitDays } from "utils/date";
import TruckCubeCountTracker from "components/Trucks/TruckCubeCountTracker/TruckCubeCountTracker";

interface ProcessedTrucksCardProps {
	truck: models["TruckViewModel"];
	onFlyoutOpen: () => void;
}

const ProcessedTrucksCard: FC<ProcessedTrucksCardProps> = ({ truck, onFlyoutOpen }) => {
	const statusTooltip: Record<"in production" | "complete", string> = {
		"in production": InProductionTooltipText,
		complete: CompleteTooltipText
	};

	const truckStatus = (truck.truckStatus ?? "").toLowerCase().replace("_", " ") as "in production" | "complete";

	return (
		<ProcessedTrucksCardWrapper
			elevation={1}
			square
			data-testid="processed-trucks-card"
		>
			<TruckHeader>
				<HeaderLeft>
					<TruckIdContainer>
						<span data-testid="truck-number"># {truck.truckNumber ?? TruckPendingText}</span>
					</TruckIdContainer>
					<StatusEtaContainer>
						<CardTooltip
							placement="bottom-start"
							title={statusTooltip[truckStatus]}
						>
							<StatusChip
								data-testid="truck-status"
								label={convertToTitleCase(truckStatus)}
							/>
						</CardTooltip>
						<Eta>
							<span>{ETAText}</span>
							<span data-testid="truck-requested-delivery-date">
								{dateFormatTwoDigitDays(truck?.stops?.[0]?.orders?.[0]?.eta) ??
									UnavailableDataPlaceholderText}
							</span>
						</Eta>
					</StatusEtaContainer>
				</HeaderLeft>
				<HeaderRight>
					<TruckCubeCountTracker truck={truck} />
				</HeaderRight>
			</TruckHeader>
			<TruckDetails
				onClick={onFlyoutOpen}
				data-id="processed-trucks-card"
			>
				<DetailsListItemTwoColumns>
					<DetailsListItemStrong>{AccountNumberText}</DetailsListItemStrong>
					<DetailsListItemParagraph data-testid="truck-account-name">
						{[truck.accountNumber, truck.accountName].filter(Boolean).join(" - ")}
					</DetailsListItemParagraph>
				</DetailsListItemTwoColumns>
				<DetailsListItemTwoColumns>
					<DetailsListItemStrong>{TruckNameText}</DetailsListItemStrong>
					<DetailsListItemParagraph data-testid="truck-name">{truck.truckName}</DetailsListItemParagraph>
				</DetailsListItemTwoColumns>
				<DetailsListItemTwoColumns>
					<DetailsListItemStrong>{ProductLineText}</DetailsListItemStrong>
					<DetailsListItemParagraph data-testid="truck-product-line">
						{truck.productLine?.description}
					</DetailsListItemParagraph>
				</DetailsListItemTwoColumns>
				<DetailsListItem>
					<DetailsListItemStrong>{CabinetCountText}</DetailsListItemStrong>
					<DetailsListItemParagraph data-testid="truck-cab-count">
						{truck.cabinetCount}
					</DetailsListItemParagraph>
				</DetailsListItem>
				<DetailsListItemTwoColumns>
					<DetailsListItemStrong>{OrderDateText}</DetailsListItemStrong>
					<DetailsListItemParagraph data-testid="truck-order-date">
						{truck.submittedOn ? dateFormatTwoDigitDays(truck.submittedOn) : "N/A"}
					</DetailsListItemParagraph>
				</DetailsListItemTwoColumns>
				<ArrowContainer>
					<ArrowIcon />
				</ArrowContainer>
			</TruckDetails>
		</ProcessedTrucksCardWrapper>
	);
};

export default ProcessedTrucksCard;
